import { Injector, Service, inject } from '@angular/core';
import { LoginResponse, OidcSecurityService, UserDataResult } from 'angular-auth-oidc-client';
import { Observable, firstValueFrom, filter } from 'rxjs';
import { ErrorHandlingService } from '@errors/error-handling.service';
import { DataContext } from '@services/data-context';
import { LanguageService } from '@services/language';

export interface OidcUserClaims {
  id?: string;
  sub?: string;
  name?: string;
  preferred_username?: string;
  email?: string;
  roles?: string[];
  company_id?: string;
  company_name?: string;
  locale?: string;
}

@Service()
export class AuthService {
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly dataContext = inject(DataContext);
  // ErrorHandlingService must be resolved LAZILY: its chain reaches HttpClient,
  // whose auth interceptor injects AuthService back — an eager inject() here
  // creates a circular dependency (NG0200) while AuthService is constructing.
  private readonly injector = inject(Injector);
  private readonly languageService = inject(LanguageService);

  readonly user = this.dataContext.loggedInUser;

  constructor() {
    this.setupAuthEventHandlers();
  }

  private setupAuthEventHandlers(): void {
    this.oidcSecurityService.checkSessionChanged$.subscribe((isChanged) => {
      if (isChanged) {
        this.handleLogout();
      }
    });

    this.oidcSecurityService.userData$.pipe(
      filter(userData => !!userData)
    ).subscribe((userData) => {
      void this.getLoggedInUserDetails(userData.userData as OidcUserClaims | null)
        .catch((error: unknown) => this.injector.get(ErrorHandlingService).silentLogError(error));
    });
  }

  async getUser(): Promise<OidcUserClaims | null> {
    const { userData } = await firstValueFrom(
      this.oidcSecurityService.checkAuth()
    );
    if (userData) {
      await this.getLoggedInUserDetails(userData as OidcUserClaims);
    }
    return (userData as OidcUserClaims | null) ?? null;
  }

  private async getLoggedInUserDetails(userData: OidcUserClaims | null): Promise<void> {
    if (userData) {
      const language = this.languageService.mapLocaleToLanguage(userData.locale);

      this.dataContext.setLoggedInUser({
        id: userData.id || userData.sub || '',
        name: userData.name || userData.preferred_username || '',
        email: userData.email || '',
        companyId: userData.company_id,
        companyName: userData.company_name,
        language
      });
    }
  }

  login(): void {
    const redirect = JSON.stringify({
      pathname: location.pathname,
      queryParams: location.search.substring(1),
    });
    localStorage.setItem('auth_redirect', redirect);
    this.oidcSecurityService.authorize();
  }

  async logout(): Promise<void> {
    this.handleLogout();
    await firstValueFrom(this.oidcSecurityService.logoff());
  }

  private handleLogout(): void {
    this.dataContext.clearUser();
  }

  async completeAuthentication(): Promise<{ isAuthenticated: boolean }> {
    const result = await firstValueFrom(
      this.oidcSecurityService.checkAuth(this.getCallbackUrlForStsMatch())
    );
    if (result.isAuthenticated && result.userData) {
      await this.getLoggedInUserDetails(result.userData as OidcUserClaims);
    }
    return { isAuthenticated: result.isAuthenticated };
  }

  /**
   * Rebuilds the current callback URL with the trailing slash the identity
   * server requires on the redirect URI (…/auth-callback/).
   *
   * The identity server only accepts `http://localhost:4300/auth-callback/`
   * (with the trailing slash) as a registered redirect URI, so it is kept in
   * the OIDC config and sent on the authorize request. Angular's router,
   * however, normalises the browser URL to `/auth-callback` (no slash) before
   * this callback handler runs. angular-auth-oidc-client's `checkAuth()`
   * compares its configured `redirectUrl` against the current URL to decide
   * whether the response is a callback from the STS; the missing slash makes
   * that comparison fail ("configured redirectUrl does not match with the
   * current url"), so the authorization code is never exchanged and the app
   * bounces back to the login page in an endless loop. Passing an explicitly
   * slash-normalised URL keeps the comparison — and the code exchange — working.
   */
  private getCallbackUrlForStsMatch(): string {
    const { origin, pathname, search, hash } = window.location;
    const normalizedPath = pathname.endsWith('/') ? pathname : `${pathname}/`;
    return `${origin}${normalizedPath}${search}${hash}`;
  }

  async isAuthenticated(): Promise<boolean> {
    const { isAuthenticated } = await firstValueFrom(
      this.oidcSecurityService.checkAuth()
    );
    return isAuthenticated;
  }

  async renewToken(): Promise<LoginResponse> {
    return await firstValueFrom(
      this.oidcSecurityService.forceRefreshSession()
    );
  }

  checkAuth(): Observable<LoginResponse> {
    return this.oidcSecurityService.checkAuth();
  }

  getAccessToken(): Observable<string> {
    return this.oidcSecurityService.getAccessToken();
  }

  getUserData(): Observable<UserDataResult> {
    return this.oidcSecurityService.userData$;
  }
}
